import { useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePresignUpload, useStartIngestion, useEnrollments, useGeneralExams, useExamSections, useMe, useMaterials, useDeleteMaterial } from '../lib/api';
import { useIngestion } from '../lib/ingestion-context';
import type { SourceType, StudyMaterial } from '@shared/types';

// ── Helpers ──────────────────────────────────────────────────────────────────

type InputMode = 'paste' | 'web_url' | 'youtube' | 'quizlet';

const INPUT_MODES: { key: InputMode; label: string }[] = [
  { key: 'paste', label: 'Paste' },
  { key: 'web_url', label: 'Web URL' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'quizlet', label: 'Quizlet' },
];

function mimeToSourceType(mime: string, name: string): SourceType {
  if (mime === 'application/pdf') return 'pdf';
  if (mime.includes('word') || name.endsWith('.docx') || name.endsWith('.doc')) return 'docx';
  if (mime.includes('presentation') || name.endsWith('.pptx') || name.endsWith('.ppt')) return 'pptx';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'text/plain' || name.endsWith('.txt')) return 'txt';
  return 'pdf'; // fallback — worker will detect
}

const ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.webp,.mp3,.m4a,.wav';

/** Strip everything except plain text and a safe set of punctuation. */
function sanitizeFocusText(raw: string): string {
  // Allow: letters, digits, spaces, newlines, and these punctuation: . ! ? " " ( ) - _ # $ % ^ & * + ,  ; : '
  return raw.replace(/[^\w\s.,;:!?'"()\-_#$%^&*+\n]/g, '').slice(0, 1000);
}

// ── Component ────────────────────────────────────────────────────────────────

export function UploadPage() {
  const [searchParams] = useSearchParams();
  const preselectedClass = searchParams.get('class') ?? '';

  const [mode, setMode] = useState<InputMode>('paste');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [target, setTarget] = useState<'class' | 'exam'>('class');
  const [classSectionId, setClassSectionId] = useState(preselectedClass);
  const [examSectionId, setExamSectionId] = useState('');
  const [examId, setExamId] = useState('');
  const [questionsRequested, setQuestionsRequested] = useState(20);
  const [passageMode, setPassageMode] = useState(false);
  const [passagesRequested, setPassagesRequested] = useState(3);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [focusInstructions, setFocusInstructions] = useState('');
  const [questionTypes, setQuestionTypes] = useState<Record<string, boolean>>({ mcq: true, sata: true, 'passage-mcq': true });
  const [selectedMaterials, setSelectedMaterials] = useState<StudyMaterial[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const { data: materialsData } = useMaterials();
  const deleteMaterial = useDeleteMaterial();
  const materials = materialsData?.materials ?? [];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { data: enrollments } = useEnrollments();
  const { data: me } = useMe();
  const { data: exams } = useGeneralExams();
  const { data: examSections } = useExamSections(target === 'exam' ? examId : null);
  const presign = usePresignUpload();
  const ingest = useStartIngestion();
  const { trackJobs } = useIngestion();

  // Detect if selected exam is MCAT
  const selectedExamName = useMemo(() => {
    if (target !== 'exam' || !examId) return '';
    return exams?.exams.find((x: any) => x.id === examId)?.name ?? '';
  }, [target, examId, exams]);
  const isMcat = /\bmcat\b/i.test(selectedExamName);
  const isNursingExam = /\b(nclex|ngn|ancc|aanp|pmhn|fnp)\b/i.test(selectedExamName);
  const isAncc = /\bancc\b/i.test(selectedExamName);
  // Determine which question types are available based on exam
  const availableQuestionTypes = useMemo(() => {
    if (target === 'exam') {
      if (isMcat) return ['mcq', 'passage-mcq'] as const;
      if (isAncc) return ['mcq', 'sata'] as const;
      if (isNursingExam) return ['mcq', 'sata'] as const;
      return []; // Other exams: MCQ only, no toggles needed
    }
    // Class sections: show all types (nursing-style)
    return ['mcq', 'sata'] as const;
  }, [target, isMcat, isAncc, isNursingExam]);
  const hasUnlimitedIngestion = me?.user.role === 'admin' ||
    me?.user.role === 'superadmin' ||
    me?.user.email.toLowerCase() === 'angelakhryselle@gmail.com';

  // Auto-enable passage mode when MCAT is selected
  const effectivePassageMode = isMcat && passageMode;

  // ── File handling ────────────────────────────────────────────────────────

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const arr = Array.from(incoming);
    if (arr.length === 0) return;
    setFiles((prev) => [...prev, ...arr]);
  }, []);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  // ── Build user instructions string ──────────────────────────────────────

  const buildUserInstructions = (): string | undefined => {
    const parts: string[] = [];

    // MCAT question type preferences
    if (isMcat && availableQuestionTypes.length > 0) {
      const wantDiscrete = questionTypes['mcq'] ?? true;
      const wantPassage = questionTypes['passage-mcq'] ?? true;
      if (wantPassage && !wantDiscrete) {
        parts.push('Question format: Generate ONLY passage-based MCQ questions. Each passage must be a comprehensive 150-300 word science passage or 500-600 word CARS passage with a minimum of 5 linked MCQ questions per passage. Do NOT generate any discrete standalone questions.');
      } else if (wantDiscrete && !wantPassage) {
        parts.push('Question format: Generate ONLY discrete standalone MCQ questions (no passages). Each question should be self-contained with a 60-200 word stem.');
      }
      // If both selected, no instruction needed (default behavior)
    }

    // Non-MCAT question type preferences
    if (!isMcat && availableQuestionTypes.length > 0) {
      const selected = availableQuestionTypes.filter(t => questionTypes[t]);
      if (selected.length > 0 && selected.length < availableQuestionTypes.length) {
        const typeLabels: Record<string, string> = { mcq: 'Multiple Choice (MCQ)', sata: 'Select All That Apply (SATA)' };
        parts.push(`Question types: Generate ONLY these types: ${selected.map(t => typeLabels[t] ?? t).join(', ')}.`);
      }
    }

    // MCAT passage minimum enforcement (always when passage mode is on)
    if (isMcat && effectivePassageMode) {
      parts.push('MCAT passage requirement: Every passage MUST have a minimum of 5 questions. Each passage must be comprehensive with full experimental context, data, and methodology. Every question must include a complete explanation with letter-prefix format covering all 4 answer choices and a pro_explanation with deeper reasoning.');
    }

    // Free-text focus (sanitized)
    const cleanFocus = sanitizeFocusText(focusInstructions);
    if (cleanFocus.trim()) {
      parts.push(cleanFocus.trim());
    }

    return parts.length > 0 ? parts.join('\n') : undefined;
  };

  // ── Submit ───────────────────────────────────────────────────────────────

  const buildTarget = () => ({
    classSectionId: target === 'class' ? classSectionId : undefined,
    generalExamSectionId: target === 'exam' ? examSectionId : undefined,
    visibility: 'private' as const,
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState('');

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const newJobIds: string[] = [];
    const userInstructions = buildUserInstructions();

    try {
      // Reuse mode: generate from previously-saved material (pooled if multiple).
      if (selectedMaterials.length > 0) {
        setUploadProgress(`Generating from ${selectedMaterials.length} saved ${selectedMaterials.length === 1 ? 'item' : 'items'}...`);
        const r = await ingest.mutateAsync({
          sourceType: 'paste',
          materialIds: selectedMaterials.map((m) => m.id),
          questionsRequested,
          userInstructions,
          target: buildTarget(),
        });
        trackJobs([r.job.id]);
        setSelectedMaterials([]);
        setUploadProgress('');
        navigate('/');
        return;
      }

      // Count total sources to split questions across
      const hasTextContent = (mode === 'paste' || mode === 'quizlet') && text.trim().length > 0;
      const hasUrlContent = (mode === 'web_url' || mode === 'youtube') && url.trim().length > 0;
      const sourceCount = (hasTextContent || hasUrlContent ? 1 : 0) + files.length;
      const perSource = Math.max(5, Math.round(questionsRequested / Math.max(sourceCount, 1)));

      // 1. Submit text/URL source if present
      if (hasTextContent || hasUrlContent) {
        setUploadProgress(effectivePassageMode
          ? `Generating ${passagesRequested} passages...`
          : 'Submitting content...');
        const r = await ingest.mutateAsync({
          sourceType: (mode === 'paste' || mode === 'quizlet') ? 'paste' : mode,
          text: (mode === 'paste' || mode === 'quizlet') ? text : undefined,
          sourceUri: (mode !== 'paste' && mode !== 'quizlet') ? url : undefined,
          questionsRequested: effectivePassageMode ? passagesRequested * 7 : (sourceCount === 1 ? questionsRequested : perSource),
          passageMode: effectivePassageMode,
          passagesRequested: effectivePassageMode ? passagesRequested : undefined,
          userInstructions,
          target: buildTarget(),
        });
        newJobIds.push(r.job.id);
      }

      // 2. Upload each file and start ingestion
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        setUploadProgress(`Uploading ${file.name} (${i + 1}/${files.length})...`);
        const p = await presign.mutateAsync({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        });
        await fetch(p.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });

        setUploadProgress(effectivePassageMode
          ? `Generating ${passagesRequested} passages from ${file.name}...`
          : `Starting AI generation for ${file.name}...`);
        // Last source gets the remainder to hit the exact total
        const isLast = i === files.length - 1 && !hasTextContent && !hasUrlContent;
        const thisCount = isLast ? questionsRequested - perSource * i : perSource;

        const r = await ingest.mutateAsync({
          sourceType: mimeToSourceType(file.type, file.name),
          uploadId: p.uploadId,
          questionsRequested: effectivePassageMode ? passagesRequested * 7 : Math.max(5, thisCount),
          passageMode: effectivePassageMode,
          passagesRequested: effectivePassageMode ? passagesRequested : undefined,
          userInstructions,
          target: buildTarget(),
        });
        newJobIds.push(r.job.id);
      }

      trackJobs(newJobIds);
      setText('');
      setUrl('');
      setFiles([]);
      setUploadProgress('');
      // Navigate home so user can continue studying while generation runs
      navigate('/');
    } catch (err: any) {
      console.error('[upload] submit failed', err);
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
      // If we got some jobs started before the error, track them
      if (newJobIds.length > 0) trackJobs(newJobIds);
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  const canSubmit =
    !submitting &&
    (target === 'class' ? !!classSectionId : !!examSectionId) &&
    (selectedMaterials.length > 0 || text.trim().length > 0 || url.trim().length > 0 || files.length > 0);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <h1 className="text-2xl font-semibold text-squishy-dark tracking-tight">Add Study Content</h1>

      {/* ── Source mode tabs ─────────────────────────────────────────────── */}
      <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
        {INPUT_MODES.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setMode(key)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
              mode === key
                ? 'bg-white text-squishy-dark shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Content area ────────────────────────────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {mode === 'paste' && (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = `${Math.min(el.scrollHeight, 360)}px`;
            }}
            rows={1}
            className="w-full px-4 py-3 text-sm text-slate-800 placeholder-slate-400 resize-none focus:outline-none overflow-y-auto"
            placeholder="Paste your notes here..."
          />
        )}
        {mode === 'web_url' && (
          <div className="p-4">
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full text-sm text-slate-800 placeholder-slate-400 focus:outline-none"
              placeholder="https://example.com/article"
            />
          </div>
        )}
        {mode === 'youtube' && (
          <div className="p-4">
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full text-sm text-slate-800 placeholder-slate-400 focus:outline-none"
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        )}
        {mode === 'quizlet' && (
          <div className="p-4 space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
              <p className="font-semibold mb-1.5">How to export from Quizlet:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs leading-relaxed">
                <li>Open your Quizlet set on <strong>quizlet.com</strong></li>
                <li>Click the <strong>⋯</strong> (more) menu</li>
                <li>Select <strong>Export</strong></li>
                <li>Click <strong>Copy text</strong></li>
                <li>Paste it below</li>
              </ol>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              className="w-full text-sm text-slate-800 placeholder-slate-400 resize-none focus:outline-none"
              placeholder="Paste your exported Quizlet terms here..."
            />
          </div>
        )}
      </section>

      {/* ── Universal file upload zone ──────────────────────────────────── */}
      <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              // Copy files immediately — iOS can lose the reference
              const copied = Array.from(e.target.files);
              addFiles(copied);
            }
            // Defer reset so iOS doesn't clobber the FileList
            setTimeout(() => { if (fileInputRef.current) fileInputRef.current.value = ''; }, 100);
          }}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className={`w-full flex items-center justify-center gap-2 border rounded-xl py-3 px-4 text-sm font-semibold transition active:scale-[0.99] shadow-sm ${
            isDragOver
              ? 'border-squishy-teal bg-teal-50 text-squishy-tealDark'
              : 'border-slate-200 bg-white text-squishy-tealDark hover:bg-slate-50'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
          </svg>
          {isDragOver ? 'Drop to attach' : 'Attach a file'}
          <span className="hidden sm:inline text-xs font-normal text-slate-400">PDF, Word, PPT, image, audio</span>
        </button>
      </div>

      {/* ── Attached files list ──────────────────────────────────────────── */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
              <div className="w-8 h-8 rounded-lg bg-squishy-teal/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-squishy-tealDark" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{f.name}</p>
                <p className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                className="text-slate-300 hover:text-red-400 transition-colors p-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Generate from saved material (history) ───────────────────────── */}
      {(materials.length > 0 || selectedMaterials.length > 0) && (
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-squishy-dark">Generate from past material</h2>
            {materials.length > 0 && (
              <button onClick={() => setShowHistory(true)} className="text-xs font-semibold text-squishy-primary hover:underline">
                View all ({materials.length})
              </button>
            )}
          </div>

          {selectedMaterials.length > 0 ? (
            <div className="space-y-2">
              <p className="text-[11px] text-slate-400">
                Pooling {selectedMaterials.length} selected {selectedMaterials.length === 1 ? 'item' : 'items'}. Choose a class/exam and count below, then Generate.
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedMaterials.map((m) => (
                  <span key={m.id} className="inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg pl-2.5 pr-1.5 py-1 text-xs font-medium">
                    <span className="max-w-[160px] truncate">{m.title}</span>
                    <button onClick={() => setSelectedMaterials((s) => s.filter((x) => x.id !== m.id))} className="text-teal-400 hover:text-teal-600 text-sm leading-none">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            materials.slice(0, 1).map((m) => (
              <button key={m.id} onClick={() => setShowHistory(true)} className="w-full flex items-center gap-3 text-left bg-slate-50 rounded-xl p-3 hover:bg-slate-100 transition active:scale-[0.99]">
                <MaterialIcon sourceType={m.sourceType} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-squishy-navy truncate">{m.title}</div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {m.context ? `${m.context.label} · ` : ''}{m.questionsGenerated} questions · {m.flashcardsGenerated} flashcards
                  </div>
                </div>
                <span className="text-xs text-squishy-primary font-semibold shrink-0">Reuse &rarr;</span>
              </button>
            ))
          )}
        </section>
      )}

      {/* ── Destination ──────────────────────────────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-700">Destination</h2>
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
          <button onClick={() => setTarget('class')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${target === 'class' ? 'bg-white text-squishy-dark shadow-sm' : 'text-slate-500'}`}>
            My Class
          </button>
          <button onClick={() => setTarget('exam')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${target === 'exam' ? 'bg-white text-squishy-dark shadow-sm' : 'text-slate-500'}`}>
            General Exam
          </button>
        </div>

        {target === 'class' ? (
          <select value={classSectionId} onChange={(e) => setClassSectionId(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-squishy-teal/30 focus:border-squishy-teal">
            <option value="">Select a class section...</option>
            {enrollments?.enrollments?.map((e: any) => (
              <option key={e.id} value={e.id}>
                {e.school_name} — {e.class_code} {e.professor_name ? `(Prof. ${e.professor_name})` : '(All teachers)'}
              </option>
            ))}
          </select>
        ) : (
          <div className="space-y-2">
            <select value={examId} onChange={(e) => { setExamId(e.target.value); setExamSectionId(''); }}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-squishy-teal/30 focus:border-squishy-teal">
              <option value="">Select an exam...</option>
              {exams?.exams.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <select value={examSectionId} onChange={(e) => setExamSectionId(e.target.value)} disabled={!examId}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 bg-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-squishy-teal/30 focus:border-squishy-teal">
              <option value="">Select a section...</option>
              {examSections?.sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}

        {/* ── MCAT Passage Mode Toggle ───────────────────────────────────── */}
        {isMcat && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={passageMode}
                onChange={(e) => setPassageMode(e.target.checked)}
                className="w-4 h-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <span className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">Passage-Based Questions</span>
                <p className="text-xs text-indigo-600 mt-0.5">
                  Generate MCAT-style passages with 7 questions each
                </p>
              </div>
            </label>

            {passageMode && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-indigo-700">
                  <span>Generate</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={passagesRequested}
                    onChange={(e) => setPassagesRequested(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)))}
                    className="w-14 border border-indigo-200 rounded-lg px-2 py-1 text-center text-sm text-indigo-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                  />
                  <span>passage{passagesRequested !== 1 ? 's' : ''}</span>
                </div>
                <div className="text-xs text-indigo-500 bg-indigo-100/50 dark:bg-indigo-500/15 rounded-lg px-3 py-2">
                  {passagesRequested} passage{passagesRequested !== 1 ? 's' : ''} x 7 questions = <strong>{passagesRequested * 7} questions</strong>
                  <br />
                  Each passage includes a 150-600 word scenario with 7 linked MCQ questions. Min 5 approved per passage guaranteed.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Question count (discrete mode) ─────────────────────────────── */}
        {!effectivePassageMode && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>Generate</span>
              <input
                type="number"
                min={5}
                max={hasUnlimitedIngestion ? undefined : 50}
                value={questionsRequested}
                onChange={(e) => {
                  const next = Math.max(5, parseInt(e.target.value, 10) || 5);
                  setQuestionsRequested(hasUnlimitedIngestion ? next : Math.min(50, next));
                }}
                className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-center text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-squishy-teal/30"
              />
            <span>questions total</span>
          </div>
        )}
      </section>

      {/* ── AI Focus Instructions (Optional) ─────────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => setShowInstructions(!showInstructions)}
          className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-squishy-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
            </svg>
            <span className="text-sm font-semibold text-slate-700">AI Focus Instructions</span>
            <span className="text-xs text-slate-400 font-normal">Optional</span>
          </div>
          <svg className={`w-4 h-4 text-slate-400 transition-transform ${showInstructions ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </button>

        {showInstructions && (
          <div className="px-5 pb-5 space-y-4 border-t border-slate-100">
            <p className="text-xs text-slate-500 pt-3 leading-relaxed">
              Tell the AI what to focus on. Choose question types and give brief direction on which topics, chapters, or concepts to emphasize. Skip this if you want the AI to decide based on your material. Answer accuracy, explanations, and quality checks are always enforced regardless of instructions.
            </p>

            {/* Question type toggles */}
            {availableQuestionTypes.length > 1 && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Question Types</label>
                <div className="flex flex-wrap gap-2">
                  {availableQuestionTypes.map(type => {
                    const labels: Record<string, string> = {
                      mcq: 'MCQ',
                      sata: 'SATA',
                      'passage-mcq': 'Passage-Based',
                    };
                    const descriptions: Record<string, string> = {
                      mcq: isMcat ? 'Discrete standalone' : 'Single best answer',
                      sata: 'Select all that apply',
                      'passage-mcq': '5+ questions per passage',
                    };
                    const checked = questionTypes[type] ?? true;
                    const canUncheck = availableQuestionTypes.filter(t => questionTypes[t]).length > 1;
                    return (
                      <label
                        key={type}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm ${
                          checked
                            ? 'border-squishy-teal bg-teal-50/60 dark:bg-teal-500/15 text-squishy-tealDark dark:text-teal-300'
                            : 'border-slate-200 text-slate-400 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (!checked || canUncheck) {
                              setQuestionTypes(prev => ({ ...prev, [type]: !prev[type] }));
                            }
                          }}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-squishy-teal focus:ring-squishy-teal/30"
                        />
                        <div>
                          <span className="font-medium">{labels[type]}</span>
                          <span className="text-xs text-slate-400 ml-1 hidden sm:inline">({descriptions[type]})</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {isMcat && (
                  <p className="text-xs text-indigo-500">
                    Passage-based questions include a 150-600 word passage with a minimum of 5 linked questions. Discrete questions are standalone with no passage.
                  </p>
                )}
              </div>
            )}

            {/* Free-text focus instructions */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Focus Area</label>
              <textarea
                value={focusInstructions}
                onChange={(e) => setFocusInstructions(sanitizeFocusText(e.target.value))}
                rows={3}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-squishy-teal/30 focus:border-squishy-teal"
                placeholder={isMcat
                  ? 'e.g. Focus on amino acid chemistry and enzyme kinetics, emphasize experimental design questions, cover CARS passages about ethics...'
                  : 'e.g. Focus on cardiovascular pharmacology, emphasize drug interactions, make questions about Chapter 12 content...'}
              />
              <div className="flex justify-between">
                <p className="text-xs text-slate-400">
                  Brief direction on topics, chapters, or concepts to emphasize
                </p>
                <span className={`text-xs ${focusInstructions.length > 900 ? 'text-amber-500 font-medium' : 'text-slate-400'}`}>{focusInstructions.length}/1000</span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ── Error display ────────────────────────────────────────────────── */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <span className="font-medium">Error:</span> {submitError}
        </div>
      )}

      {/* ── Submit button ────────────────────────────────────────────────── */}
      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full bg-squishy-primary hover:bg-squishy-tealDark text-white font-semibold py-3 rounded-xl disabled:opacity-40 transition-colors shadow-sm"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeLinecap="round" className="opacity-30" />
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeDashoffset="45" strokeLinecap="round" />
            </svg>
            {uploadProgress || 'Submitting...'}
          </span>
        ) : effectivePassageMode
          ? `Generate ${passagesRequested} Passage${passagesRequested !== 1 ? 's' : ''} (${passagesRequested * 7} Questions)`
          : selectedMaterials.length > 0
            ? `Generate from ${selectedMaterials.length} saved ${selectedMaterials.length === 1 ? 'item' : 'items'}`
            : 'Generate Questions'}
      </button>

      {showHistory && (
        <MaterialHistoryModal
          materials={materials}
          initialSelected={selectedMaterials}
          onClose={() => setShowHistory(false)}
          onDone={(sel) => { setSelectedMaterials(sel); setShowHistory(false); }}
          onDelete={(id) => deleteMaterial.mutate(id)}
        />
      )}
    </div>
  );
}

// ── Material history multi-select modal ──────────────────────────────────────

function MaterialIcon({ sourceType }: { sourceType: string }) {
  const label = sourceType === 'paste' || sourceType === 'txt' ? 'TXT'
    : sourceType === 'web_url' ? 'URL'
    : sourceType === 'youtube' ? 'YT'
    : sourceType.toUpperCase().slice(0, 4);
  return (
    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-squishy-dark to-purple-700 flex items-center justify-center text-white text-[9px] font-bold shrink-0">
      {label}
    </div>
  );
}

function MaterialHistoryModal({ materials, initialSelected, onClose, onDone, onDelete }: {
  materials: StudyMaterial[];
  initialSelected: StudyMaterial[];
  onClose: () => void;
  onDone: (selected: StudyMaterial[]) => void;
  onDelete: (id: string) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set(initialSelected.map((m) => m.id)));
  const toggle = (id: string) => setPicked((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const daysLeft = (iso: string) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-squishy-navy">Your material</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
          </div>
          <p className="text-xs text-slate-500 mt-1">Select one or more to generate more questions from them (combined).</p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {materials.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No saved material yet.</p>}
          {materials.map((m) => {
            const isPicked = picked.has(m.id);
            return (
              <div key={m.id} className={`flex items-center gap-3 rounded-xl border-2 p-3 transition ${isPicked ? 'border-squishy-primary bg-teal-50' : 'border-slate-200'}`}>
                <button onClick={() => toggle(m.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${isPicked ? 'bg-squishy-primary border-squishy-primary' : 'border-slate-300'}`}>
                    {isPicked && <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 111.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>}
                  </div>
                  <MaterialIcon sourceType={m.sourceType} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-squishy-navy truncate">{m.title}</div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {m.context ? `${m.context.label} · ` : ''}{m.questionsGenerated} questions · {m.flashcardsGenerated} flashcards · {daysLeft(m.expiresAt)}d left
                    </div>
                  </div>
                </button>
                <button onClick={() => onDelete(m.id)} className="text-slate-300 hover:text-red-400 transition p-1 shrink-0" title="Delete">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                </button>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t">
          <button
            onClick={() => onDone(materials.filter((m) => picked.has(m.id)))}
            disabled={picked.size === 0}
            className="w-full bg-squishy-primary text-white py-3 rounded-xl font-semibold text-base disabled:opacity-40 active:scale-[0.98] transition"
          >
            Done{picked.size > 0 ? ` (${picked.size})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
